import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, InputNumber, Segmented, Select, Space, Typography } from 'antd';
import type { PriceResponseDto } from '../../api-schema/generated/types';
import type { Portfolio } from '../../hooks/usePortfolios';
import { boardError, priceBoardApi } from '../../services/priceBoardApi';
import { formatInt, formatNumber, formatPercent, numberInputProps } from '../../utils/numberFormat';
import { boardPrice, isFutureSymbol, lotOf, pctOf, tickOf, toApiPrice, TONE_COLOR, toneOf } from './boardMath';

// Right-aligned depth figures keep a margin on both sides: flush cells cut the last column.
const depthCell: React.CSSProperties = { textAlign: 'right', padding: '2px 8px 2px 4px' };

const { Text } = Typography;

interface Props {
    symbol: string;
    quote?: PriceResponseDto;
    portfolios: Portfolio[];
    onPlaced: () => void;
}

const Stat: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div style={{ minWidth: 0 }}>
        <div style={{ color: '#848E9C', fontSize: 11 }}>{label}</div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{children}</div>
    </div>
);

/** Selected symbol: its band, day range and top of book, and the order ticket. */
const SymbolPanel: React.FC<Props> = ({ symbol, quote: q, portfolios, onPlaced }) => {
    const { message, modal } = App.useApp();
    const future = isFutureSymbol(symbol);
    const tone = (v?: number | null) => TONE_COLOR[toneOf(v, q?.ref_price, q?.ceiling_price, q?.floor_price)];
    const px = (v?: number | null) => <span style={{ color: tone(v) }}>{boardPrice(symbol, v)}</span>;

    // Futures book to a derivatives account, stocks to an equity one.
    const accounts = useMemo(
        () => portfolios.filter((p) => (p.accountType ?? 'EQUITY') === (future ? 'DERIVATIVES' : 'EQUITY')),
        [portfolios, future],
    );
    const [accountId, setAccountId] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (!accounts.some((a) => a.id === accountId)) setAccountId(accounts[0]?.id);
    }, [accounts, accountId]);
    const account = accounts.find((a) => a.id === accountId);

    const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
    const [type, setType] = useState<'LIMIT' | 'MARKET'>('LIMIT');
    const [qty, setQty] = useState<number | null>(lotOf(symbol));
    const [price, setPrice] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);

    // A new symbol starts from its own lot and last price.
    useEffect(() => {
        setQty(lotOf(symbol));
        setPrice(null);
    }, [symbol]);
    useEffect(() => {
        if (price == null && q?.latest_matched_price) setPrice(q.latest_matched_price);
    }, [q?.latest_matched_price, price]);

    const levels = [1, 2, 3].map((i) => ({
        bid: (q as any)?.[`bid_price_${i}`] as number | undefined,
        bidQ: (q as any)?.[`bid_quantity_${i}`] as number | undefined,
        ask: (q as any)?.[`ask_price_${i}`] as number | undefined,
        askQ: (q as any)?.[`ask_quantity_${i}`] as number | undefined,
    }));

    const submit = () => {
        if (!account) return;
        const lot = lotOf(symbol);
        if (!qty || qty <= 0 || qty % lot !== 0) {
            message.warning(`Quantity must be a multiple of ${lot}`);
            return;
        }
        if (type === 'LIMIT' && !(price && price > 0)) {
            message.warning('Enter a price');
            return;
        }
        const priceText = type === 'MARKET' ? 'market' : boardPrice(symbol, price);
        modal.confirm({
            title: `${side === 'BUY' ? 'Buy' : 'Sell'} ${formatInt(qty)} ${symbol} at ${priceText}?`,
            content: (
                <div style={{ fontSize: 12 }}>
                    <div>Account: {account.portfolioName}</div>
                    {type === 'MARKET' && <div>Sent as a limit at the {side === 'BUY' ? 'ceiling' : 'floor'}: fills at the best price available.</div>}
                    {account.brokerMode === 'CLIENT' && <div>Sent to your desktop terminal: it must be running with execution on.</div>}
                    <div>This order belongs to no bot.</div>
                </div>
            ),
            okText: side === 'BUY' ? 'Buy' : 'Sell',
            okButtonProps: { danger: side === 'SELL' },
            onOk: async () => {
                setBusy(true);
                try {
                    await priceBoardApi.place({
                        portfolioId: account.id,
                        symbol,
                        side,
                        orderType: type,
                        quantity: qty,
                        ...(type === 'LIMIT' && price != null && { price: toApiPrice(symbol, price) }),
                    });
                    message.success('Order sent');
                    onPlaced();
                } catch (e) {
                    message.error(boardError(e, 'Order not sent'));
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const color = side === 'BUY' ? '#0ECB81' : '#F6465D';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{symbol}</span>
                <span style={{ fontSize: 22, fontWeight: 700 }}>{px(q?.latest_matched_price)}</span>
                <span style={{ color: tone(q?.latest_matched_price) }}>{formatPercent(pctOf(q?.latest_matched_price, q?.ref_price), 2, { signed: true })}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                <Stat label="Ceiling"><span style={{ color: TONE_COLOR.ceiling }}>{boardPrice(symbol, q?.ceiling_price)}</span></Stat>
                <Stat label="Ref"><span style={{ color: TONE_COLOR.ref }}>{boardPrice(symbol, q?.ref_price)}</span></Stat>
                <Stat label="Floor"><span style={{ color: TONE_COLOR.floor }}>{boardPrice(symbol, q?.floor_price)}</span></Stat>
                <Stat label="Volume">{formatInt(q?.total_matched_quantity)}</Stat>
                <Stat label="Open">{px(q?.open_price)}</Stat>
                <Stat label="High">{px(q?.highest_price)}</Stat>
                <Stat label="Low">{px(q?.lowest_price)}</Stat>
                {future ? (
                    <Stat label="Open interest">{formatInt(q?.open_interest)}</Stat>
                ) : (
                    <Stat label="Foreign net">{formatInt(q?.foreign_buy_quantity != null && q?.foreign_sell_quantity != null ? q.foreign_buy_quantity - q.foreign_sell_quantity : null, { signed: true })}</Stat>
                )}
            </div>

            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', tableLayout: 'fixed', fontVariantNumeric: 'tabular-nums' }}>
                <thead>
                    <tr style={{ color: '#848E9C' }}>
                        <th style={{ ...depthCell, fontWeight: 400 }}>Bid vol</th>
                        <th style={{ ...depthCell, fontWeight: 400 }}>Bid</th>
                        <th style={{ ...depthCell, fontWeight: 400 }}>Ask</th>
                        <th style={{ ...depthCell, fontWeight: 400 }}>Ask vol</th>
                    </tr>
                </thead>
                <tbody>
                    {levels.map((l, i) => (
                        <tr key={i}>
                            <td style={depthCell}>{formatInt(l.bidQ)}</td>
                            <td style={{ ...depthCell, cursor: 'pointer' }} onClick={() => l.bid && setPrice(l.bid)}>{px(l.bid)}</td>
                            <td style={{ ...depthCell, cursor: 'pointer' }} onClick={() => l.ask && setPrice(l.ask)}>{px(l.ask)}</td>
                            <td style={depthCell}>{formatInt(l.askQ)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={{ borderTop: '1px solid #2B3139', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Segmented
                    block
                    value={side}
                    onChange={(v) => setSide(v as 'BUY' | 'SELL')}
                    options={[{ label: 'Buy', value: 'BUY' }, { label: 'Sell', value: 'SELL' }]}
                />
                <Select
                    size="small"
                    value={accountId}
                    onChange={setAccountId}
                    placeholder={future ? 'No derivatives account' : 'No equity account'}
                    options={accounts.map((a) => ({ value: a.id, label: `${a.portfolioName}${a.brokerMode === 'CLIENT' ? ' · terminal' : ''}` }))}
                />
                <Segmented
                    block
                    size="small"
                    value={type}
                    onChange={(v) => setType(v as 'LIMIT' | 'MARKET')}
                    options={[{ label: 'Limit', value: 'LIMIT' }, { label: 'Market', value: 'MARKET' }]}
                />
                <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                        {...numberInputProps()}
                        style={{ width: '50%' }}
                        min={0}
                        step={tickOf(symbol, price)}
                        value={type === 'MARKET' ? null : price}
                        disabled={type === 'MARKET'}
                        placeholder={type === 'MARKET' ? 'Market' : future ? 'Price (points)' : 'Price (×1,000)'}
                        onChange={(v) => setPrice(v == null ? null : Number(v))}
                    />
                    <InputNumber
                        {...numberInputProps()}
                        style={{ width: '50%' }}
                        min={lotOf(symbol)}
                        step={lotOf(symbol)}
                        value={qty}
                        placeholder={future ? 'Contracts' : 'Shares'}
                        onChange={(v) => setQty(v == null ? null : Number(v))}
                    />
                </Space.Compact>
                {!future && type === 'LIMIT' && price != null && qty != null && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Value ≈ {formatNumber(Math.round(price * 1000 * qty))} VND
                    </Text>
                )}
                {account?.brokerMode === 'CLIENT' && (
                    <Alert type="info" showIcon style={{ fontSize: 11, padding: '4px 8px' }} message="Terminal account: your desktop app sends this order." />
                )}
                <Button
                    type="primary"
                    block
                    loading={busy}
                    disabled={!account}
                    onClick={submit}
                    style={{ background: account ? color : undefined, borderColor: account ? color : undefined, fontWeight: 600 }}
                >
                    {side === 'BUY' ? 'Buy' : 'Sell'} {symbol}
                </Button>
            </div>
        </div>
    );
};

export default SymbolPanel;
